// components/modals/ComplaintModal.js
// UPDATED: Real-time WebSocket support for customer and agent messages

import React, { useState, useEffect, useRef } from 'react';
import {
  X, Send, History, Bot, FileText, User, Sparkles,
  Clock, Mail, Phone, MessageSquare, Globe, AlertTriangle,
  ChevronDown, RefreshCw, UserCheck, Lock, Eye, Wand2, MessageCircle,
  Zap, Image, Paperclip
} from 'lucide-react';
import { PRIORITY_COLORS, STATUS_COLORS } from '../../utils/constants';
import webSocketService from '../../services/websocket';

const ComplaintModal = ({ complaint, isOpen, onClose, onComplaintUpdate, api, user }) => {
  // Conversation state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  
  // Message composition state
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // AI improvement state
  const [isImprovingText, setIsImprovingText] = useState(false);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [improvedText, setImprovedText] = useState('');
  const [showImprovedText, setShowImprovedText] = useState(false);
  
  // Legacy state
  const [customerHistory, setCustomerHistory] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [responseTemplates, setResponseTemplates] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  
  // Complaint metadata state
  const [assignedTo, setAssignedTo] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // Refs
  const messagesEndRef = useRef(null);
  const messageTextareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Helper function to get the display name for assigned person
  const getAssignedDisplayName = () => {
    if (assignedToName) {
      return assignedToName;
    }

    if (complaint?.assignedAgent && typeof complaint.assignedAgent === 'object') {
      return complaint.assignedAgent.name || complaint.assignedAgent.email || 'Unassigned';
    }
    
    if (assignedTo && !assignedTo.startsWith('cm') && !assignedTo.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return assignedTo;
    }
    
    if (assignedTo && teamMembers.length > 0) {
      const member = teamMembers.find(m => m.id === assignedTo);
      if (member) {
        return member.name;
      }
    }
    
    if (user && (assignedTo === user.id || complaint?.assignedTo === user.id)) {
      return user.name || user.email;
    }
    
    return 'Unassigned';
  };

  // Helper function to get sending method label
  const getSendingMethodLabel = (message, complaint) => {
    if (message.messageType !== 'OUTBOUND') return null;
    
    switch (complaint.source) {
      case 'FACEBOOK':
        return (
          <span className="flex items-center gap-1">
            <MessageCircle className="w-3 h-3" />
            Sent via Facebook
          </span>
        );
      case 'EMAIL':
      default:
        return (
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            Sent via email
          </span>
        );
    }
  };

  // Helper function to check if message is auto-acknowledgement
  const isAutoAcknowledgement = (message) => {
    if (message.messageType !== 'OUTBOUND') return false;
    if (message.isAutoAcknowledgement) return true;
    if (message.author?.id === 'system' || message.author?.email === 'system') return true;
    
    const autoAckIndicators = [
      'auto-acknowledgment',
      'auto-acknowledgement',
      'automated response',
      'automatic response'
    ];
    
    const contentLower = message.content?.toLowerCase() || '';
    return autoAckIndicators.some(indicator => contentLower.includes(indicator));
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to get authenticated image URL
  const getAuthenticatedImageUrl = (path) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return `${api.baseURL}${path}`;
    return `${api.baseURL}${path}?token=${encodeURIComponent(token)}`;
  };

  // Helper function to check if email is a synthetic Facebook email
  const isFacebookEmail = (email) => {
    return email && email.includes('facebook_') && email.includes('@ticketdesk.app');
  };

  // Helper function to get customer email display
  const getCustomerEmailDisplay = () => {
    const email = complaint.customer?.email || complaint.customerEmail;

    // Don't show synthetic Facebook emails
    if (isFacebookEmail(email)) {
      return 'Facebook Messenger User';
    }

    return email || 'No email available';
  };

  useEffect(() => {
    if (complaint && isOpen) {
      console.log('Complaint data:', complaint);
      loadConversation();
      loadCustomerHistory();
      loadTeamMembers();
      loadResponseTemplates();
      setMessageContent('');
      setImprovedText('');
      setShowImprovedText(false);
      setSelectedTemplate('');
      
      if (complaint.assignedAgent && typeof complaint.assignedAgent === 'object') {
        const name = complaint.assignedAgent.name || complaint.assignedAgent.email || '';
        setAssignedTo(complaint.assignedAgent.id || '');
        setAssignedToName(name);
      } else if (complaint.assignedTo && typeof complaint.assignedTo === 'string') {
        const isId = complaint.assignedTo.startsWith('cm') || 
                     complaint.assignedTo.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
        
        if (isId) {
          setAssignedTo(complaint.assignedTo);
          setAssignedToName('');
        } else {
          setAssignedTo('');
          setAssignedToName(complaint.assignedTo);
        }
      } else {
        setAssignedTo('');
        setAssignedToName('');
      }
      
      setStatus(complaint.status || 'NEW');
      setPriority(complaint.priority || 'MEDIUM');
    }
  }, [complaint, isOpen]);

  useEffect(() => {
    if (assignedTo && !assignedToName && teamMembers.length > 0) {
      const member = teamMembers.find(m => m.id === assignedTo);
      if (member) {
        setAssignedToName(member.name);
      }
    }
  }, [teamMembers, assignedTo, assignedToName]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ✅ FIXED: WebSocket listener for real-time messages
  useEffect(() => {
    if (!complaint?.ticketId || !isOpen) {
      console.log('⚠️ Modal not ready:', { 
        hasTicketId: !!complaint?.ticketId, 
        isOpen 
      });
      return;
    }
    
    console.log('🔌 Setting up WebSocket listener for ticket:', complaint.ticketId);
    
    webSocketService.joinTicket(complaint.ticketId);
    
    const handleNewMessage = (data) => {
      console.log('📨 NEW MESSAGE EVENT RECEIVED!');
      console.log('📨 Full data:', data);
      console.log('📨 Message Type:', data.messageType);
      console.log('📨 My ticket ID:', complaint.ticketId);
      console.log('📨 Message ticket ID:', data.ticketId);
      console.log('📨 Match?', data.ticketId === complaint.ticketId);
      
      if (data.conversationId && data.ticketId === complaint.ticketId) {
        console.log('✅ Adding message to conversation');
        
        setMessages(prev => {
          const newMessage = {
            id: data.messageId || data.conversationId,
            content: data.content || data.message,
            messageType: data.messageType || 'OUTBOUND',
            fromEmail: data.fromEmail,
            createdAt: data.createdAt || data.timestamp,
            sentAt: data.sentAt || data.timestamp,
            isRead: data.messageType === 'OUTBOUND',
            hasAttachments: data.hasAttachments || false,
            attachments: data.attachments || null,
            author: data.author || {
              name: data.fromEmail,
              email: data.fromEmail
            }
          };

          console.log('New message object:', newMessage);
          return [...prev, newMessage];
        });

        // Wait longer for images to load before scrolling
        if (data.hasAttachments) {
          setTimeout(() => scrollToBottom(), 500);
        } else {
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    };
    
    webSocketService.on('new_message', handleNewMessage);
    console.log('✅ Listener attached for new_message event');
    
    return () => {
      console.log('🔌 Cleaning up WebSocket listener for ticket:', complaint.ticketId);
      webSocketService.off('new_message', handleNewMessage);
      webSocketService.leaveTicket(complaint.ticketId);
    };
  }, [complaint?.ticketId, isOpen]);

  const loadConversation = async () => {
    if (!complaint?.id) return;
    
    setIsLoadingConversation(true);
    try {
      const response = await api.getConversation(complaint.id);
      if (response.success && response.conversation) {
        setConversation(response.conversation);
        setMessages(response.conversation.messages || []);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setConversation(null);
      setMessages([]);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const loadResponseTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const data = await api.getResponseTemplates();
      if (data && data.length > 0) {
        setResponseTemplates(data);
      } else {
        const defaultTemplates = [
          {
            id: 'default-1',
            name: 'Acknowledgment',
            content: "Dear {customerName},\n\nThank you for contacting us regarding \"{subject}\". We've received your complaint and are reviewing it carefully. Our team will respond within 24 hours with an update.\n\nBest regards,\n{agentName}",
            category: 'general'
          },
          {
            id: 'default-2',
            name: 'Resolution',
            content: "Dear {customerName},\n\nWe're pleased to inform you that your issue has been resolved. Please let us know if you need any further assistance or if you're still experiencing any problems.\n\nBest regards,\n{agentName}",
            category: 'general'
          },
          {
            id: 'default-3',
            name: 'Investigation',
            content: "Dear {customerName},\n\nWe're currently investigating your complaint in detail. Our team needs to gather more information to provide you with an accurate resolution. We'll update you within 48 hours.\n\nBest regards,\n{agentName}",
            category: 'general'
          }
        ];
        
        try {
          for (const template of defaultTemplates) {
            await api.saveResponseTemplate(template);
          }
          setResponseTemplates(defaultTemplates);
        } catch (error) {
          console.error('Error saving default templates:', error);
          setResponseTemplates(defaultTemplates);
        }
      }
    } catch (error) {
      console.error('Failed to load response templates:', error);
      setResponseTemplates([
        {
          id: 'fallback-1',
          name: 'Acknowledgment',
          content: "Thank you for contacting us. We have received your complaint and will respond within 24 hours.",
          category: 'general'
        }
      ]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.getTeamMembers();
      if (response && response.members) {
        const members = response.members.map(m => ({
          id: m.id,
          name: m.name || m.email,
          initials: (m.name || m.email).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        }));
        setTeamMembers(members);
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const statuses = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
  const priorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

  const loadCustomerHistory = async () => {
    if (!complaint || !complaint.customer) {
      setCustomerHistory([]);
      setIsLoadingHistory(false);
      return;
    }
    
    const customerId = typeof complaint.customer === 'string' ? complaint.customer : complaint.customer.id;
    
    if (!customerId) {
      setCustomerHistory([]);
      setIsLoadingHistory(false);
      return;
    }
    
    setIsLoadingHistory(true);
    try {
      const history = await api.getCustomerHistory(customerId);
      setCustomerHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Failed to load customer history:', error);
      setCustomerHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

const generateAIResponse = async () => {
  setIsGeneratingResponse(true);
  setShowImprovedText(false);
  
  try {
    const response = await api.generateAIResponse(complaint.id);
    
    if (response && response.suggestion) {
      setImprovedText(response.suggestion);
      setShowImprovedText(true);
    } else {
      alert('Failed to generate AI response');
    }
  } catch (error) {
    console.error('Failed to generate AI response:', error);
    alert('Failed to generate AI response. Please try again.');
  } finally {
    setIsGeneratingResponse(false);
  }
};

  const improveTextWithAI = async () => {
    if (!messageContent.trim()) {
      alert('Please write some text first');
      return;
    }

    setIsImprovingText(true);
    setShowImprovedText(false);
    
    try {
      const response = await api.improveText(messageContent, {
        context: 'customer_service_reply',
        customerName: complaint.customer?.name || 'Customer',
        subject: complaint.subject
      });
      
      if (response && response.improvedText) {
        setImprovedText(response.improvedText);
        setShowImprovedText(true);
      } else {
        setImprovedText(improveFallback(messageContent));
        setShowImprovedText(true);
      }
    } catch (error) {
      console.error('Failed to improve text:', error);
      setImprovedText(improveFallback(messageContent));
      setShowImprovedText(true);
    } finally {
      setIsImprovingText(false);
    }
  };

  const improveFallback = (text) => {
    let improved = text.trim();
    improved = improved.charAt(0).toUpperCase() + improved.slice(1);
    if (!improved.match(/[.!?]$/)) {
      improved += '.';
    }
    if (!improved.toLowerCase().startsWith('dear') && 
        !improved.toLowerCase().startsWith('hello') && 
        !improved.toLowerCase().startsWith('hi')) {
      const customerName = complaint.customer?.name || 'Customer';
      improved = `Dear ${customerName},\n\n${improved}`;
    }
    if (!improved.toLowerCase().includes('regards') && 
        !improved.toLowerCase().includes('sincerely')) {
      improved += '\n\nBest regards,\nCustomer Service Team';
    }
    return improved;
  };

  const handleUseImprovedText = () => {
    setMessageContent(improvedText);
    setShowImprovedText(false);
    setImprovedText('');
  };

  const replaceTemplateVariables = (content, complaint, user) => {
    const now = new Date();
    const currentDate = now.toLocaleDateString();
    const currentTime = now.toLocaleTimeString();
    const currentDateTime = now.toLocaleString();

    const customerName = complaint.customer?.name || complaint.customerName || 'Valued Customer';
    const customerEmail = complaint.customer?.email || complaint.customerEmail || '';
    const customerFirstName = customerName.split(' ')[0] || 'Valued Customer';

    const agentName = user?.name || user?.email || 'Customer Service Team';
    const agentFirstName = agentName.split(' ')[0] || 'Customer Service';
    const agentEmail = user?.email || '';

    const subject = complaint.subject || 'your inquiry';
    const complaintId = complaint.id?.slice(-6).toUpperCase() || 'COMP-001';
    const priority = complaint.priority || 'MEDIUM';
    const status = complaint.status || 'NEW';
    const category = complaint.category || 'General';
    const source = complaint.source || 'Email';
    const createdDate = complaint.createdAt ? new Date(complaint.createdAt).toLocaleDateString() : currentDate;

    const companyName = 'TicketDesk';

    return content
      .replace(/\{customerName\}/g, customerName)
      .replace(/\{customerFirstName\}/g, customerFirstName)
      .replace(/\{customerEmail\}/g, customerEmail)
      .replace(/\{myName\}/g, agentName)
      .replace(/\{agentName\}/g, agentName)
      .replace(/\{myFirstName\}/g, agentFirstName)
      .replace(/\{agentFirstName\}/g, agentFirstName)
      .replace(/\{myEmail\}/g, agentEmail)
      .replace(/\{agentEmail\}/g, agentEmail)
      .replace(/\{subject\}/g, subject)
      .replace(/\{complaintId\}/g, complaintId)
      .replace(/\{priority\}/g, priority)
      .replace(/\{status\}/g, status)
      .replace(/\{category\}/g, category)
      .replace(/\{source\}/g, source)
      .replace(/\{submissionDate\}/g, createdDate)
      .replace(/\{currentDate\}/g, currentDate)
      .replace(/\{currentTime\}/g, currentTime)
      .replace(/\{currentDateTime\}/g, currentDateTime)
      .replace(/\{companyName\}/g, companyName);
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId) {
      const template = responseTemplates.find(t => t.id === templateId);
      if (template && template.content) {
        const personalizedContent = replaceTemplateVariables(template.content, complaint, user);
        setMessageContent(personalizedContent);
        setShowImprovedText(false);
        setImprovedText('');
      }
    } else {
      setMessageContent('');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isUnder10MB = file.size <= 10 * 1024 * 1024;
      if (!isImage) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      if (!isUnder10MB) {
        alert(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim() && selectedFiles.length === 0) return;

    setIsSending(true);
    try {
      let response;

      if (complaint.source === 'FACEBOOK') {
        response = await api.sendFacebookMessage(complaint.id, messageContent, selectedFiles);
      } else {
        response = await api.addConversationMessage(complaint.id, messageContent, false, selectedFiles);
      }

      if (response.success) {
        setMessages(prev => [...prev, response.message]);
        setMessageContent('');
        setSelectedFiles([]);
        setSelectedTemplate('');
        setImprovedText('');
        setShowImprovedText(false);
        await loadConversation();
        onComplaintUpdate();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMsg = error.message || 'Failed to send message. Please try again.';
      alert(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    setShowStatusDropdown(false);
    
    try {
      await api.updateComplaintStatus(complaint.id, newStatus);
      onComplaintUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAssigneeChange = async (member) => {
    const newAssignee = member.id || null;
    const newAssigneeName = member.name || '';
    
    setAssignedTo(newAssignee);
    setAssignedToName(newAssigneeName);
    setShowAssignDropdown(false);
    
    try {
      await api.updateComplaintAssignee(complaint.id, newAssignee);
      onComplaintUpdate();
    } catch (error) {
      console.error('Failed to update assignee:', error);
      if (complaint.assignedAgent && typeof complaint.assignedAgent === 'object') {
        setAssignedTo(complaint.assignedAgent.id || '');
        setAssignedToName(complaint.assignedAgent.name || complaint.assignedAgent.email || '');
      } else {
        setAssignedTo(complaint.assignedTo || '');
        setAssignedToName('');
      }
    }
  };

  const handleAssignToMe = () => {
    if (user && user.id) {
      const currentUser = {
        id: user.id,
        name: user.name || user.email,
        initials: (user.name || user.email).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      };
      handleAssigneeChange(currentUser);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    setPriority(newPriority);
    setShowPriorityDropdown(false);
    
    try {
      await api.updateComplaintPriority(complaint.id, newPriority);
      onComplaintUpdate();
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageIcon = (message) => {
    const messageType = message.messageType;
    const isAutoAck = isAutoAcknowledgement(message);
    
    if (isAutoAck) {
      return <Bot className="w-4 h-4 text-purple-600" />;
    }
    
    switch (messageType) {
      case 'INBOUND':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'OUTBOUND':
        return <Send className="w-4 h-4 text-green-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-600" />;
    }
  };

  const getMessageTypeLabel = (message) => {
    const isAutoAck = isAutoAcknowledgement(message);
    
    if (isAutoAck) {
      return 'AI';
    }
    
    switch (message.messageType) {
      case 'INBOUND':
        return 'Customer';
      case 'OUTBOUND':
        return '';
      default:
        return 'System';
    }
  };

  if (!isOpen || !complaint) return null;

  const getSourceIcon = (source) => {
    switch (source) {
      case 'email':
      case 'EMAIL':
        return <Mail className="w-4 h-4" />;
      case 'phone':
      case 'PHONE':
        return <Phone className="w-4 h-4" />;
      case 'chat':
      case 'CHAT':
        return <MessageSquare className="w-4 h-4" />;
      case 'web':
      case 'WEB':
        return <Globe className="w-4 h-4" />;
      case 'FACEBOOK':
        return <MessageCircle className="w-4 h-4" />;
      default:
        return <Mail className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority?.toUpperCase()) {
      case 'URGENT':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'HIGH':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'LOW':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b">
          <div className="flex items-center justify-between p-6 pb-3">
            <div className="flex items-center space-x-2">
              <h2 className="text-xl font-semibold">Ticket Conversation</h2>
              <span className="text-sm text-gray-500 font-mono">
                #{complaint.id?.length > 8 ? complaint.id.slice(-6).toUpperCase() : complaint.id || 'COMP-001'}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Control Bar */}
          <div className="px-6 pb-4 flex items-center gap-4 flex-wrap">
            {/* Assignee */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Assigned:</span>
              <div className="relative">
                <button
                  onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                  className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">
                    {getAssignedDisplayName().split(' ').map(n => n[0]).join('') || 'UN'}
                  </div>
                  <span className="text-sm">{getAssignedDisplayName() || 'Unassigned'}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showAssignDropdown && (
                  <div className="absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[150px]">
                    <button
                      onClick={handleAssignToMe}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 text-purple-600 font-medium border-b"
                    >
                      Assign to me
                    </button>
                    <button
                      onClick={() => handleAssigneeChange({ id: null, name: 'Unassigned', initials: 'UN' })}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      Unassigned
                    </button>
                    {teamMembers.map(member => (
                      <button
                        key={member.id}
                        onClick={() => handleAssigneeChange(member)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">
                          {member.initials}
                        </div>
                        {member.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className={`flex items-center gap-2 px-3 py-1 rounded-lg ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}
                >
                  <span className="text-sm font-medium">{status}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showStatusDropdown && (
                  <div className="absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                    {statuses.map(s => (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Priority */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Severity:</span>
              <div className="relative">
                <button
                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                  className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${getPriorityColor(priority)}`}
                >
                  <span className="text-sm font-medium">{priority}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showPriorityDropdown && (
                  <div className="absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                    {priorities.map(p => (
                      <button
                        key={p}
                        onClick={() => handlePriorityChange(p)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Customer Info & History */}
          <div className="w-1/4 p-6 overflow-y-auto border-r bg-gray-50">
            <div className="mb-6">
              <h3 className="flex items-center text-lg font-medium mb-3">
                <User className="h-5 w-5 mr-2" />
                Customer Information
              </h3>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-medium">
                  {complaint.customer?.name || complaint.customerName || 'Unknown Customer'}
                </p>
                <p className="text-sm text-gray-600">
                  {getCustomerEmailDisplay()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Submitted: {complaint.createdAt ? new Date(complaint.createdAt).toLocaleString() : 'Unknown date'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    PRIORITY_COLORS[complaint.priority] || 'bg-gray-100 text-gray-600'
                  }`}>
                    {complaint.priority || 'MEDIUM'}
                  </span>
                  {complaint.source && getSourceIcon(complaint.source)}
                </div>
              </div>
            </div>

            <div>
              <h3 className="flex items-center text-lg font-medium mb-3">
                <History className="h-5 w-5 mr-2" />
                Customer History
              </h3>
              {isLoadingHistory ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
                </div>
              ) : customerHistory && customerHistory.length > 0 ? (
                <div className="space-y-3">
                  {customerHistory.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{item.subject}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.status === 'RESOLVED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No previous complaints from this customer</p>
              )}
            </div>
          </div>

          {/* Center Panel - Conversation */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {isLoadingConversation ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              ) : messages.length > 0 ? (
                messages.map((message) => {
                  const isAutoAck = isAutoAcknowledgement(message);
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.messageType === 'OUTBOUND' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-4 ${
                          message.messageType === 'OUTBOUND'
                            ? isAutoAck 
                              ? 'bg-purple-100 text-purple-900 border border-purple-200'
                              : 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {getMessageIcon(message)}
                          <span className="text-xs font-medium opacity-75">
                            {getMessageTypeLabel(message)}
                            {message.author && message.author.id !== 'system' && 
                              ` • ${message.author.name || message.author.email}`}
                          </span>
                          {isAutoAck && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-200 text-purple-800 rounded-full text-xs font-medium">
                              <Zap className="w-3 h-3" />
                              Auto-sent
                            </span>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap text-sm">{message.content}</p>

                        {/* Image Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {message.attachments.map((attachment, idx) => (
                              <div key={idx} className="rounded-lg overflow-hidden border border-gray-200">
                                <img
                                  src={getAuthenticatedImageUrl(attachment.path)}
                                  alt={attachment.filename}
                                  className="max-w-full h-auto cursor-pointer hover:opacity-90"
                                  style={{ maxWidth: '500px', maxHeight: '400px', objectFit: 'contain' }}
                                  onClick={() => window.open(getAuthenticatedImageUrl(attachment.path), '_blank')}
                                  title={`Click to open ${attachment.filename}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2 text-xs opacity-60">
                          <span>{formatMessageTime(message.sentAt || message.createdAt)}</span>
                          {getSendingMethodLabel(message, complaint)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No conversation messages yet</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Composition */}
            <div className="border-t p-6 bg-gray-50">
              {/* Toolbar: Template, AI buttons, and Attach Images on same line */}
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="flex-1 min-w-[180px] p-2 border rounded-md focus:outline-none focus:border-purple-500 text-sm"
                >
                  <option value="">Select template...</option>
                  {responseTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={selectedFiles.length >= 5}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                  title="Attach images (max 5)"
                >
                  <Image className="w-4 h-4" />
                  <span className="hidden sm:inline">Attach</span>
                  {selectedFiles.length > 0 && (
                    <span className="bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {selectedFiles.length}
                    </span>
                  )}
                </button>

                <button
                  onClick={generateAIResponse}
                  disabled={isGeneratingResponse || isImprovingText}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm border border-gray-300"
                >
                  {isGeneratingResponse ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1.5">AI Response</span>
                    </>
                  )}
                </button>

                <button
                  onClick={improveTextWithAI}
                  disabled={isImprovingText || isGeneratingResponse || !messageContent.trim()}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm border border-gray-300"
                >
                  {isImprovingText ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1.5">Improve</span>
                    </>
                  )}
                </button>
              </div>
              

              {showImprovedText && improvedText && (
                <div className="mb-4">
                  <h4 className="flex items-center text-sm font-medium mb-2">
                    <Bot className="h-4 w-4 mr-1" />
                    AI-Improved Text
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-4 mb-3">
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{improvedText}</p>
                  </div>
                  <button
                    onClick={handleUseImprovedText}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                  >
                    Use Improved Text
                  </button>
                </div>
              )}

              {/* File Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Selected Images ({selectedFiles.length}/5)
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          onClick={() => handleRemoveFile(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 truncate rounded-b-lg">
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <textarea
                  ref={messageTextareaRef}
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  rows={4}
                  className="w-full p-3 border rounded-md focus:outline-none focus:border-purple-500"
                  placeholder={
                    complaint.source === 'FACEBOOK'
                      ? 'Type your reply (will be sent via Facebook Messenger)...'
                      : 'Type your reply...'
                  }
                />
              </div>

              <button
                onClick={handleSendMessage}
                disabled={(!messageContent.trim() && selectedFiles.length === 0) || isSending}
                className="w-full px-4 py-3 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-purple-600 hover:bg-purple-700"
              >
                {isSending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    {complaint.source === 'FACEBOOK' ? (
                      <MessageCircle className="h-5 w-5 mr-2" />
                    ) : (
                      <Send className="h-5 w-5 mr-2" />
                    )}
                    {complaint.source === 'FACEBOOK' ? 'Send via Facebook' : 'Send Reply'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintModal;